
/**
 * Safely formats a name with fallbacks if the name is null or undefined
 * @param name The name to format
 * @param id An optional ID to use in the fallback name
 * @param entityType The type of entity (e.g., "Customer", "Restaurant")
 * @returns A formatted name string
 */
export function formatSafeName(
  name: string | null | undefined,
  id?: string | null,
  entityType: string = 'Entity'
): string {
  if (name) return name;
  
  if (id) {
    // Use ID to create a fallback name (e.g., "Restaurant #a1b2c3")
    return `${entityType} #${id.substring(0, 6)}`;
  }
  
  return `Unnamed ${entityType}`;
}

/**
 * Safely lowercases a string, handling null/undefined
 * @param value The string to convert to lowercase
 * @returns Lowercase string or empty string if input is null/undefined
 */
export function safeToLowerCase(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value.toLowerCase();
}

/**
 * Performs a case-insensitive search in a string, handling null/undefined values
 * @param text The text to search in
 * @param searchTerm The search term to look for
 * @returns Boolean indicating if searchTerm exists in text
 */
export function safeSearch(text: string | null | undefined, searchTerm: string): boolean {
  if (!text || !searchTerm) return false;
  return safeToLowerCase(text).includes(safeToLowerCase(searchTerm));
}

/**
 * Safely accesses a property that might be undefined
 * @param obj The object to access
 * @param prop The property to access
 * @param defaultValue The default value to return if property doesn't exist
 * @returns The property value or default value
 */
export function safeGet<T, K extends keyof T>(
  obj: T | null | undefined,
  prop: K,
  defaultValue: T[K]
): T[K] {
  if (!obj) return defaultValue;
  return obj[prop] !== undefined ? obj[prop] : defaultValue;
}

/**
 * Safely gets a substring from a string that might be null/undefined
 * @param str The string to get substring from
 * @param start The starting index
 * @param end The ending index
 * @returns The substring or empty string if input is invalid
 */
export function safeSubstring(
  str: string | null | undefined,
  start: number,
  end?: number
): string {
  if (!str) return '';
  try {
    return str.substring(start, end);
  } catch (error) {
    console.error('Error in safeSubstring:', error);
    return '';
  }
}

/**
 * Safely gets a display name for an entity (restaurant, customer, etc.)
 * with thorough null checking
 * @param entity The entity object containing id and name
 * @param idField The field name for the ID (default: 'id')
 * @param nameField The field name for the name (default: 'name')
 * @param entityType The type of entity for the fallback name
 * @returns A formatted name string
 */
export function getDisplayName(
  entity: any | null | undefined,
  idField: string = 'id',
  nameField: string = 'name',
  entityType: string = 'Entity'
): string {
  if (!entity) return `Unnamed ${entityType}`;
  
  // Check if name exists and is not empty
  if (entity[nameField]) return entity[nameField];
  
  // If no name but ID exists, create a fallback with ID
  if (entity[idField]) {
    try {
      return `${entityType} #${String(entity[idField]).substring(0, 6)}`;
    } catch (e) {
      console.warn(`Error creating display name for entity:`, entity, e);
      return `${entityType} ID: ${String(entity[idField])}`;
    }
  }
  
  return `Unnamed ${entityType}`;
}

/**
 * Safely gets an entity ID with fallbacks
 * @param entity The entity object
 * @param idFields Array of possible ID field names to check in order
 * @returns The ID as string or 'unknown-id' if not found
 */
export function getEntityId(
  entity: any | null | undefined,
  idFields: string[] = ['id', 'restaurant_id', 'customer_id']
): string {
  if (!entity) return 'unknown-id';
  
  for (const field of idFields) {
    if (entity[field]) return String(entity[field]);
  }
  
  return 'unknown-id';
}

/**
 * Extracts JWT information for debugging purposes
 * @returns Object containing decoded JWT information or null if not found
 */
export function debugJwtInfo() {
  try {
    // Try multiple storage approaches to handle different Supabase versions
    
    // Approach 1: Check localStorage with our configured key
    const storageKey = 'supabase.auth.token';
    const storedSession = localStorage.getItem(storageKey);
    
    if (storedSession) {
      const session = JSON.parse(storedSession);
      let accessToken = null;
      
      // Format 1: Old format with currentSession
      if (session.currentSession?.access_token) {
        accessToken = session.currentSession.access_token;
      } 
      // Format 2: Direct access token
      else if (session.access_token) {
        accessToken = session.access_token;
      } 
      // Format 3: Supabase v2 format with session object
      else if (session.session?.access_token) {
        accessToken = session.session.access_token;
      }
      
      if (accessToken) {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        
        return {
          sub: payload.sub,
          roleDirectly: payload.role,
          roleInAppMetadata: payload.app_metadata?.role,
          hasAppMetadata: !!payload.app_metadata,
          appMetadataKeys: payload.app_metadata ? Object.keys(payload.app_metadata) : [],
          expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null,
          raw: payload
        };
      }
    }
    
    // Approach 2: Look for sb-xxx-auth-token in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.match(/sb-.*-auth-token/)) {
        try {
          const supabaseData = JSON.parse(localStorage.getItem(key) || '');
          if (supabaseData?.access_token) {
            const payload = JSON.parse(atob(supabaseData.access_token.split('.')[1]));
            
            return {
              sub: payload.sub,
              roleDirectly: payload.role,
              roleInAppMetadata: payload.app_metadata?.role,
              hasAppMetadata: !!payload.app_metadata,
              appMetadataKeys: payload.app_metadata ? Object.keys(payload.app_metadata) : [],
              expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null,
              raw: payload
            };
          }
        } catch (e) {
          console.error(`Error parsing potential Supabase token from key ${key}:`, e);
        }
      }
    }
    
    console.warn('No valid JWT found in localStorage');
    return null;
  } catch (error) {
    console.error('Error extracting JWT info:', error);
    return null;
  }
}
